import { error as logError } from '@eika-infrastruktur/log';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import * as request from '@eika-infrastruktur/request';

import { CONTEXT_PATH } from './index';

const QUERY_PARAM = 'caosState';
const CAOS_RESPONSE_HEADER_KEY = 'X-Eika-Caos-Response';

interface CaosState {
    status: number;
    response?: unknown;
}

interface CaosMap {
    [path: string]: CaosState;
}

const starSearch = (url: string, path: string): boolean => {
    const parts = path.split('*');
    const hit = parts.reduce((lastIndex: number, part: string) => {
        if (lastIndex > -1) {
            return url.indexOf(part, lastIndex);
        }
        return lastIndex;
    }, 0);
    return hit > -1;
};

export const compareUrl =
    (url: string) =>
    (path: string): boolean => {
        const fullPath = path.startsWith(CONTEXT_PATH) ? path : `${CONTEXT_PATH}/rest/resource${path}`;
        if (url === fullPath) return true;
        return starSearch(url, fullPath);
    };

const getFromMap = (caosMap: CaosMap, url: string): CaosState | undefined => {
    if (url in caosMap) {
        return caosMap[url];
    }
    const hit = Object.keys(caosMap).find(compareUrl(url));
    return hit ? caosMap[hit] : undefined;
};

const getState = (): string | null => new URLSearchParams(window.location.search).get(QUERY_PARAM);
const extractCaosState = (url: string): string | undefined => {
    const state = getState();
    if (!state) return undefined;
    const parsed: CaosState | CaosMap = JSON.parse(atob(state));
    if ((parsed as CaosState).status) {
        return state;
    }
    const caosState = getFromMap(parsed as CaosMap, url);
    return caosState ? btoa(JSON.stringify(caosState)) : undefined;
};

const getOptions = (url: string, options: RequestInit) => {
    const caosState = extractCaosState(url);
    if (!caosState) {
        return options;
    }
    return {
        ...options,
        headers: {
            ...options.headers,
            [CAOS_RESPONSE_HEADER_KEY]: caosState,
        },
    };
};
export const get = <R>(url: string, options: RequestInit = {}): Promise<R> => {
    try {
        return request.get<R>(url, getOptions(url, options));
    } catch (exception) {
        if (!(exception instanceof RequestExceptionWithResponse)) {
            logError(exception);
        }
        throw exception;
    }
};

export const put = <R>(url: string, body?: unknown, options: RequestInit = {}): Promise<R> => {
    try {
        return request.put<R>(url, body, getOptions(url, options));
    } catch (exception) {
        if (!(exception instanceof RequestExceptionWithResponse)) {
            logError(exception);
        }
        throw exception;
    }
};

export const post = <R>(url: string, body?: unknown, options: RequestInit = {}): Promise<R> => {
    try {
        return request.post<R>(url, body, getOptions(url, options));
    } catch (exception) {
        if (!(exception instanceof RequestExceptionWithResponse)) {
            logError(exception);
        }
        throw exception;
    }
};

export const del = <R>(url: string, options: RequestInit = {}): Promise<R> => {
    try {
        return request.del<R>(url, getOptions(url, options));
    } catch (exception) {
        if (!(exception instanceof RequestExceptionWithResponse)) {
            logError(exception);
        }
        throw exception;
    }
};

export const patch = <R>(url: string, body?: unknown, options: RequestInit = {}): Promise<R> => {
    try {
        return request.patch<R>(url, body, getOptions(url, options));
    } catch (exception) {
        if (!(exception instanceof RequestExceptionWithResponse)) {
            logError(exception);
        }
        throw exception;
    }
};
